import { useEffect, useState } from "react";
import { PawPrint, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { SubscribersPanel } from "@/components/admin/SubscribersPanel";
import { FreebieForm } from "@/components/admin/FreebieForm";

const TABS = [
  { id: "freebies", label: "Freebies" },
  { id: "subscribers", label: "Subscribers" },
];

export default function Admin() {
  const [auth, setAuth] = useState(null); // null = checking, false = logged out
  const [tab, setTab] = useState("freebies");
  const [view, setView] = useState({ name: "list" });

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/auth/me")
      .then(({ data }) => setAuth(data))
      .catch(() => setAuth(false));
  }, []);

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory" data-testid="admin-checking">
        <PawPrint className="h-8 w-8 animate-pulse text-[#6E8CA3]" fill="#6E8CA3" strokeWidth={0} />
      </div>
    );
  }

  if (auth === false) {
    return <AdminLogin onSuccess={setAuth} />;
  }

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setAuth(false);
      toast.success("Logged out");
    }
  };

  if (view.name === "form") {
    return (
      <FreebieForm
        item={view.item || null}
        onDone={() => setView({ name: "list", refresh: Date.now() })}
        onCancel={() => setView({ name: "list", refresh: Date.now() })}
      />
    );
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-shell">
      <header className="border-b border-ink/10 bg-ivory">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#A8BFCE]/40">
              <PawPrint className="h-5 w-5 text-[#6E8CA3]" fill="#6E8CA3" strokeWidth={0} />
            </span>
            <div>
              <p className="font-serif text-xl font-medium leading-tight tracking-tight text-ink">
                My Dachshund Days
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/50">
                Admin · {auth.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="logout-button"
            onClick={logout}
            aria-label="Log out"
            className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-full border border-ink/15 text-ink/70 transition-colors hover:border-ink/40"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <nav className="mx-auto flex w-full max-w-5xl gap-2 px-4 pb-4 sm:px-6" aria-label="Admin sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`min-h-[44px] rounded-full px-6 text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                tab === t.id ? "bg-[#A8BFCE] text-ink" : "bg-white/70 text-ink/55 hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      {tab === "freebies" ? (
        <AdminDashboard
          key={view.refresh || 0}
          onAdd={() => setView({ name: "form" })}
          onEdit={(item) => setView({ name: "form", item })}
        />
      ) : (
        <SubscribersPanel />
      )}
    </div>
  );
}
